// Laden des Referenzvideos für Export und Upload (Premix-Ton + MP4-Mux).
//
// Beide Pfade lasen das Admin-Video früher per nacktem `fetch` von
// `/api/stream-video/…`. Die Route hängt am Media-Session-Cookie (12 h,
// decisions.md 2026-09-21). Stand ein Tab über Nacht offen, antwortete sie mit
// 401 — `audioExport` prüfte `res.ok` nicht, dekodierte den Fehler-Body,
// scheiterte und lieferte still einen Premix ohne Video-Ton (Audit M18).
//
// Hier liegt deshalb die eine Stelle, die (a) den Status prüft und (b) bei 401
// einmal die Media-Session erneuert und es dann noch einmal versucht. Dazu
// kommt eine kleine Byte-Prüfung, ob die Datei überhaupt eine Tonspur hat —
// damit ein stummes Referenzvideo keine Warnung auslöst.

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface VideoFetchOptions {
  signal?: AbortSignal;
  /** Holt ein frisches Media-Session-Cookie (`ensureMediaSession`). Wird nur
   *  nach einem 401 aufgerufen, danach genau ein zweiter Versuch. */
  refreshMediaSession?: () => Promise<unknown>;
  /** HEAD für die Größe (`videoInputSize`), sonst GET. */
  method?: 'GET' | 'HEAD';
  /** Nur für Tests. */
  fetchImpl?: FetchLike;
}

export class VideoFetchError extends Error {
  readonly status: number | null;
  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'VideoFetchError';
    this.status = status;
  }
}

/** GET auf eine Video-URL mit Status-Prüfung und einmaligem Session-Refresh
 *  bei 401. Wirft `VideoFetchError` bei jedem Nicht-2xx; `AbortError` geht
 *  unverändert durch. */
export async function fetchVideo(
  url: string,
  opts: VideoFetchOptions = {},
): Promise<Response> {
  const f: FetchLike = opts.fetchImpl ?? ((u, init) => fetch(u, init));
  const init: RequestInit = { signal: opts.signal, credentials: 'same-origin', ...(opts.method ? { method: opts.method } : {}) };
  let res = await f(url, init);
  if (res.status === 401 && opts.refreshMediaSession) {
    // Body des Fehlers verwerfen, sonst hält der Browser die Verbindung.
    try { await res.body?.cancel(); } catch { /* ignore */ }
    await opts.refreshMediaSession();
    opts.signal?.throwIfAborted();
    res = await f(url, init);
  }
  if (!res.ok) {
    try { await res.body?.cancel(); } catch { /* ignore */ }
    throw new VideoFetchError(`Could not load video: HTTP ${res.status}`, res.status);
  }
  return res;
}

/** Wie weit vorn und hinten nach dem `moov` gesucht wird. Das `moov` liegt
 *  entweder am Anfang (fast start) oder am Ende; selbst bei langen Videos ist
 *  es nur wenige MB groß. */
const SOUND_SCAN_WINDOW = 32 * 1024 * 1024;

/** Hat die MP4/MOV-Datei eine Tonspur? Sucht die `hdlr`-Boxen der Tracks
 *  (ISO-BMFF: size, 'hdlr', version/flags, pre_defined, handler_type; bei
 *  QuickTime steht an derselben Stelle der component subtype) und prüft auf
 *  `soun`.
 *
 *  - `'yes'`     mindestens eine Tonspur gefunden
 *  - `'no'`      Track-Handler gefunden, aber keiner ist `soun` (stummes Video)
 *  - `'unknown'` kein Track-Handler in den Suchfenstern (kein MP4/MOV, z. B.
 *                WebM) — Aufrufer behandeln das wie `'yes'`
 *
 *  Liest nur, kopiert nichts; muss VOR `decodeAudioData` laufen, weil das den
 *  Puffer detacht. */
export function detectSoundTrack(bytes: Uint8Array): 'yes' | 'no' | 'unknown' {
  const len = bytes.length;
  const windows: Array<[number, number]> =
    len <= 2 * SOUND_SCAN_WINDOW
      ? [[0, len]]
      : [[0, SOUND_SCAN_WINDOW], [len - SOUND_SCAN_WINDOW, len]];
  let sawTrackHandler = false;
  for (const [from, to] of windows) {
    const end = Math.min(to, len) - 16;
    for (let i = Math.max(from, 4); i <= end; i++) {
      // 'hdlr' = 68 64 6c 72
      if (
        bytes[i] !== 0x68 || bytes[i + 1] !== 0x64 ||
        bytes[i + 2] !== 0x6c || bytes[i + 3] !== 0x72
      ) continue;
      const h = i + 12;
      const type = String.fromCharCode(bytes[h], bytes[h + 1], bytes[h + 2], bytes[h + 3]);
      if (type === 'soun') return 'yes';
      if (type === 'vide') sawTrackHandler = true;
    }
  }
  return sawTrackHandler ? 'no' : 'unknown';
}
